<template><div><h2 id="outer-shadow" tabindex="-1" class="d-docsite--header-2">Outer Shadow <a class="header-anchor d-link d-docsite--link d-link" href="#outer-shadow" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-bs-{n}</code> to add an outer box shadow to an element.</p>
<code-example source-code='&lt;div class=&quot;d-p-200 d-bar-400 d-bgc-primary d-bs-sm&quot;&gt;d-bs-sm&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-bar-400 d-bgc-primary d-bs-md&quot;&gt;d-bs-md&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-bar-400 d-bgc-primary d-bs-lg&quot;&gt;d-bs-lg&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-bar-400 d-bgc-primary d-bs-xl&quot;&gt;d-bs-xl&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-bar-400 d-bgc-primary d-bs-card&quot;&gt;d-bs-card&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="400" :direction="{ 'default': 'column', 'lg': 'row' }">
  <div class="d-p-200 d-bar-400 d-bgc-primary d-bs-sm">d-bs-sm</div>
  <div class="d-p-200 d-bar-400 d-bgc-primary d-bs-md">d-bs-md</div>
  <div class="d-p-200 d-bar-400 d-bgc-primary d-bs-lg">d-bs-lg</div>
  <div class="d-p-200 d-bar-400 d-bgc-primary d-bs-xl">d-bs-xl</div>
  <div class="d-p-200 d-bar-400 d-bgc-primary d-bs-card">d-bs-card</div>
</dt-stack>
</code-example>
<h2 id="no-shadow" tabindex="-1" class="d-docsite--header-2">No Shadow <a class="header-anchor d-link d-docsite--link d-link" href="#no-shadow" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-bs-none</code> to remove a box shadow to an element.</p>
<code-example source-code='&lt;div class=&quot;d-p-200 d-bar-400 d-bgc-primary d-bs-none&quot;&gt;.d-bs-none&lt;/div&gt;'>
<div class="d-p-200 d-bar-400 d-bgc-primary d-bs-none">.d-bs-none</div>
</code-example>
<h2 id="hover" tabindex="-1" class="d-docsite--header-2">Hover <a class="header-anchor d-link d-docsite--link d-link" href="#hover" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>h:d-bs-{n}</code> to change an element's <code v-pre>:hover</code> state box shadow.</p>
<code-example custom class="d-fl-center d-p-300 d-bgc-secondary d-w100p" source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-bar-400 d-bgc-primary h:d-bs-md&quot;&gt;Hover over me&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-p-200 d-bar-400 d-bgc-primary h:d-bs-md">Hover over me</dt-button>
</code-example>
<h2 id="focus" tabindex="-1" class="d-docsite--header-2">Focus <a class="header-anchor d-link d-docsite--link d-link" href="#focus" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>f:d-bs-{n}</code> to change an element's <code v-pre>:focus</code> and <code v-pre>:focus-within</code> state box shadow.</p>
<code-example custom class="d-fl-center d-p-300 d-bgc-secondary d-w100p" source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-bar-400 d-bgc-primary f:d-bs-md&quot;&gt;Focus me&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-p-200 d-bar-400 d-bgc-primary f:d-bs-md">Focus me</dt-button>
</code-example>
<h2 id="focus-visible" tabindex="-1" class="d-docsite--header-2">Focus Visible <a class="header-anchor d-link d-docsite--link d-link" href="#focus-visible" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>fv:d-bs-{n}</code> to change an element's <code v-pre>:focus-visible</code> state box shadow [only when focused by keyboard].</p>
<code-example custom class="d-fl-center d-p-300 d-bgc-secondary d-w100p" source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-bar-400 d-bgc-primary fv:d-bs-md&quot;&gt;Keyboard focus me&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-p-200 d-bar-400 d-bgc-primary fv:d-bs-md">Keyboard focus me</dt-button>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<utility-class-table>
  <template #content>
    <tbody>
      <tr v-for="size in boxShadowSizes">
        <th scope="row" class="d-code--sm d-docsite-code">.d-bs-{{ size }}</th>
        <td v-if="size !== 'card'" class="d-code--sm">
            box-shadow: var(--dt-shadow-{{ size }}-0-x) var(--dt-shadow-{{ size }}-0-y) var(--dt-shadow-{{ size }}-0-blur) var(--dt-shadow-{{ size }}-0-spread) var(--dt-shadow-{{ size }}-0-color) !important;
        </td>
        <td v-else class="d-code--sm">
            box-shadow: var(--dt-shadow-card-0-x) var(--dt-shadow-card-0-y) var(--dt-shadow-card-0-blur) var(--dt-shadow-card-0-spread) var(--dt-shadow-card-0-color),<br/>
            var(--dt-shadow-card-1-x) var(--dt-shadow-card-1-y) var(--dt-shadow-card-1-blur) var(--dt-shadow-card-1-spread) var(--dt-shadow-card-1-color),<br/>
            var(--dt-shadow-card-2-x) var(--dt-shadow-card-2-y) var(--dt-shadow-card-2-blur) var(--dt-shadow-card-2-spread) var(--dt-shadow-card-2-color)<br/>
            !important
        </td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm d-docsite-code">.d-bs-none</th>
        <td class="d-code--sm">box-shadow: none !important;</td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm d-docsite-code">.d-bs-unset</th>
        <td class="d-code--sm">box-shadow: unset !important;</td>
      </tr>
    </tbody>
  </template>
</utility-class-table>
</div></template>


<script setup>
const boxShadowSizes = ['sm', 'md', 'lg', 'xl', 'card'];
</script>